import React, { useCallback, useEffect, useState } from 'react';
import AdminToggle from './AdminToggle';
import { AdminPanelAlert, AdminPanelHero, AdminPanelSection } from './AdminPanelUi';

const emptyForm = () => ({
  id: 0,
  title: '',
  event_start: '',
  event_end: '',
  venue: '',
  city: '',
  country: '',
  details: '',
  external_url: '',
  is_published: true,
  sort_order: 0,
});

function adminHeaders(token) {
  const tok = String(token || '').trim();
  return tok ? { 'X-Admin-Session': tok, Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

const shortDateFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

function formatAdminDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || '—';
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : shortDateFmt.format(d);
}

function AdminShowListRow({ row, onEdit, onDelete, onTogglePublished, togglingId }) {
  const range =
    row.event_end && row.event_end !== row.event_start
      ? `${formatAdminDate(row.event_start)} → ${formatAdminDate(row.event_end)}`
      : formatAdminDate(row.event_start);

  return (
    <div className="admin-show-card card border shadow-sm mb-2">
      <div className="card-body py-3 px-3">
        <div className="admin-show-card__grid">
          <div className="admin-show-card__cell admin-show-card__cell--dates">
            <span className="admin-show-card__mob-label">Dates</span>
            <div className="admin-show-card__date-range">{range}</div>
          </div>
          <div className="admin-show-card__cell admin-show-card__cell--location">
            <span className="admin-show-card__mob-label">Location</span>
            <div className="fw-semibold text-break">{row.venue}</div>
            <div className="small text-muted text-break">{[row.city, row.country].filter(Boolean).join(', ')}</div>
          </div>
          <div className="admin-show-card__cell admin-show-card__cell--title">
            <span className="admin-show-card__mob-label">Title</span>
            <div className="small text-break">{row.title?.trim() ? row.title : <span className="text-muted">—</span>}</div>
          </div>
          <div className="admin-show-card__cell admin-show-card__cell--live">
            <span className="admin-show-card__mob-label">Live</span>
            <button
              type="button"
              className={`admin-show-live-toggle btn btn-sm rounded-pill ${
                row.is_published ? 'btn-success' : 'btn-outline-secondary'
              }`}
              onClick={() => onTogglePublished(row)}
              disabled={togglingId === row.id}
              aria-pressed={row.is_published}
              title={
                row.is_published
                  ? 'Published on website — click to hide'
                  : 'Hidden from website — click to publish'
              }
            >
              {togglingId === row.id ? '…' : row.is_published ? 'On' : 'Off'}
            </button>
          </div>
          <div className="admin-show-card__cell admin-show-card__cell--actions">
            <span className="admin-show-card__mob-label">Actions</span>
            <div className="admin-show-card__actions">
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => onEdit(row)}>
                Edit
              </button>
              <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => onDelete(row.id)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminShowsPanel({ sessionToken, setStatus }) {
  const [exhibitions, setExhibitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const r = await fetch('/api/admin_shows.php', { headers: adminHeaders(sessionToken) });
      const text = await r.text();
      let body = {};
      try {
        body = text ? JSON.parse(text) : {};
      } catch {
        setLoadError('Invalid response from server.');
        setExhibitions([]);
        return;
      }
      if (!r.ok || !body.success) {
        setLoadError(body.error || `HTTP ${r.status}`);
        setExhibitions([]);
        return;
      }
      setExhibitions(Array.isArray(body.exhibitions) ? body.exhibitions : []);
    } catch (e) {
      setLoadError(e.message || 'Network error');
      setExhibitions([]);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm());
  };

  const editRow = (row) => {
    setForm({
      id: row.id,
      title: row.title || '',
      event_start: row.event_start || '',
      event_end: row.event_end || '',
      venue: row.venue || '',
      city: row.city || '',
      country: row.country || '',
      details: row.details || '',
      external_url: row.external_url || '',
      is_published: Boolean(row.is_published),
      sort_order: Number(row.sort_order) || 0,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus('Saving exhibition…');
    try {
      const r = await fetch('/api/admin_shows.php', {
        method: 'POST',
        headers: adminHeaders(sessionToken),
        body: JSON.stringify({
          session_token: String(sessionToken || '').trim(),
          action: 'save',
          ...form,
          event_end: form.event_end ? form.event_end : null,
          external_url: form.external_url ? form.external_url.trim() : '',
        }),
      });
      const text = await r.text();
      let body = {};
      try {
        body = text ? JSON.parse(text) : {};
      } catch {
        setStatus('Save failed: response was not JSON.');
        return;
      }
      if (!r.ok || !body.success) {
        setStatus(body.error || `Save failed (HTTP ${r.status}).`);
        return;
      }
      setExhibitions(Array.isArray(body.exhibitions) ? body.exhibitions : []);
      resetForm();
      setStatus(form.id ? 'Exhibition updated.' : 'Exhibition added.');
    } catch (err) {
      setStatus(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (row) => {
    setTogglingId(row.id);
    setStatus('Updating visibility…');
    try {
      const r = await fetch('/api/admin_shows.php', {
        method: 'POST',
        headers: adminHeaders(sessionToken),
        body: JSON.stringify({
          session_token: String(sessionToken || '').trim(),
          action: 'toggle_published',
          id: row.id,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.success) {
        setStatus(body.error || 'Could not update Live status.');
        return;
      }
      setExhibitions(Array.isArray(body.exhibitions) ? body.exhibitions : []);
      const next = body.exhibitions?.find((e) => e.id === row.id);
      if (form.id === row.id && next) {
        setForm((prev) => ({ ...prev, is_published: Boolean(next.is_published) }));
      }
      setStatus(next?.is_published ? 'Show is now live on the website.' : 'Show hidden from the website.');
    } catch (err) {
      setStatus(`Could not update Live status: ${err.message}`);
    } finally {
      setTogglingId(0);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this exhibition?')) return;
    setStatus('Deleting…');
    try {
      const r = await fetch('/api/admin_shows.php', {
        method: 'POST',
        headers: adminHeaders(sessionToken),
        body: JSON.stringify({ session_token: String(sessionToken || '').trim(), action: 'delete', id }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.success) {
        setStatus(body.error || 'Delete failed.');
        return;
      }
      setExhibitions(Array.isArray(body.exhibitions) ? body.exhibitions : []);
      if (form.id === id) resetForm();
      setStatus('Deleted.');
    } catch (err) {
      setStatus(`Delete failed: ${err.message}`);
    }
  };

  return (
    <div className="admin-shows-panel admin-panel-workspace admin-panel-workspace-root">
      <AdminPanelHero
        title="Exhibitions & shows"
        lead="Manage events on your site. The next published show can appear on the home page."
      />

      {loading ? <AdminPanelAlert variant="info">Loading exhibitions…</AdminPanelAlert> : null}
      {loadError ? <AdminPanelAlert variant="warning">{loadError}</AdminPanelAlert> : null}

      <AdminPanelSection title={form.id ? 'Edit exhibition' : 'New exhibition'} subtitle="Dates, venue, and publish status.">
        <form onSubmit={save}>
          <div className="row g-3">
            <div className="col-12 col-lg-6">
              <label className="form-label small" htmlFor="show-title">
                Event title <span className="text-muted">(optional)</span>
              </label>
              <input
                id="show-title"
                className="form-control form-control-sm"
                value={form.title}
                onChange={(ev) => setField('title', ev.target.value)}
                placeholder="e.g. India International Jewellery Show"
              />
            </div>
            <div className="col-12 col-sm-6 col-lg-3">
              <label className="form-label small" htmlFor="show-start">
                Start date <span className="text-danger">*</span>
              </label>
              <input
                id="show-start"
                type="date"
                required
                className="form-control form-control-sm admin-shows-date-input"
                value={form.event_start}
                onChange={(ev) => setField('event_start', ev.target.value)}
              />
            </div>
            <div className="col-12 col-sm-6 col-lg-3">
              <label className="form-label small" htmlFor="show-end">
                End date <span className="text-muted">(optional)</span>
              </label>
              <input
                id="show-end"
                type="date"
                className="form-control form-control-sm admin-shows-date-input"
                value={form.event_end}
                onChange={(ev) => setField('event_end', ev.target.value)}
              />
            </div>
            <div className="col-12">
              <label className="form-label small" htmlFor="show-venue">
                Venue <span className="text-danger">*</span>
              </label>
              <input
                id="show-venue"
                required
                className="form-control form-control-sm"
                value={form.venue}
                onChange={(ev) => setField('venue', ev.target.value)}
                placeholder="Hall / booth / address line"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small" htmlFor="show-city">
                City <span className="text-danger">*</span>
              </label>
              <input
                id="show-city"
                required
                className="form-control form-control-sm"
                value={form.city}
                onChange={(ev) => setField('city', ev.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small" htmlFor="show-country">
                Country <span className="text-danger">*</span>
              </label>
              <input
                id="show-country"
                required
                className="form-control form-control-sm"
                value={form.country}
                onChange={(ev) => setField('country', ev.target.value)}
                placeholder="e.g. United States, India, UAE"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small" htmlFor="show-sort">
                Sort order
              </label>
              <input
                id="show-sort"
                type="number"
                className="form-control form-control-sm"
                value={form.sort_order}
                onChange={(ev) => setField('sort_order', parseInt(ev.target.value, 10) || 0)}
              />
            </div>
            <div className="col-12">
              <label className="form-label small" htmlFor="show-details">
                Details <span className="text-muted">(optional)</span>
              </label>
              <textarea
                id="show-details"
                className="form-control form-control-sm"
                rows={3}
                value={form.details}
                onChange={(ev) => setField('details', ev.target.value)}
                placeholder="Booth number, timings, or what you will display"
              />
            </div>
            <div className="col-12">
              <label className="form-label small" htmlFor="show-url">
                Link <span className="text-muted">(optional)</span>
              </label>
              <input
                id="show-url"
                type="url"
                className="form-control form-control-sm"
                value={form.external_url}
                onChange={(ev) => setField('external_url', ev.target.value)}
                placeholder="https://…"
              />
            </div>
            <div className="col-12">
              <AdminToggle
                id="show-published"
                label="Published (visible on home page)"
                checked={form.is_published}
                onChange={(v) => setField('is_published', v)}
              />
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-3">
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? 'Saving…' : form.id ? 'Update' : 'Add exhibition'}
            </button>
            {form.id > 0 && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </form>
      </AdminPanelSection>

      {!loading && exhibitions.length === 0 && !loadError ? (
        <p className="small text-muted">No exhibitions yet. Add one above.</p>
      ) : null}

      {exhibitions.length > 0 ? (
        <div className="admin-shows-list admin-panel-list-card">
          <div className="admin-panel-list-card__head">
            <div>
              <span className="admin-panel-list-card__kicker">All shows</span>
              <div className="admin-panel-list-card__title">
                Exhibitions
                <span className="admin-panel-list-card__count">{exhibitions.length}</span>
              </div>
            </div>
          </div>
          <div className="admin-panel-list-card__body p-3">
          <div className="admin-shows-grid-head d-none d-md-grid" aria-hidden="true">
            <span>Dates</span>
            <span>Location</span>
            <span>Title</span>
            <span>Live</span>
            <span className="text-end">Actions</span>
          </div>
          {exhibitions.map((row) => (
            <AdminShowListRow
              key={row.id}
              row={row}
              onEdit={editRow}
              onDelete={remove}
              onTogglePublished={togglePublished}
              togglingId={togglingId}
            />
          ))}
          </div>
        </div>
      ) : null}

      <div className="admin-panel-scroll-pad" aria-hidden="true" />
    </div>
  );
}
